import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth, SuperAdminProvider, useSuperAdmin } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Lancamentos from './pages/Lancamentos';
import FluxoCaixa from './pages/FluxoCaixa';
import Relatorios from './pages/Relatorios';
import TarefasNotas from './pages/TarefasNotas';
import Configuracoes from './pages/Configuracoes';
import Carteira from './pages/Carteira';
import CartaoCredito from './pages/CartaoCredito';
import ContasPagar from './pages/ContasPagar';
import Calendario from './pages/Calendario';
import ImportarExportar from './pages/ImportarExportar';
import Desempenho from './pages/Desempenho';
import Recorrencias from './pages/Recorrencias';
import Metas from './pages/Metas';
import WhatsAppBot from './pages/WhatsAppBot';
import Chat from './pages/Chat';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import HomeLanding from './pages/HomeLanding';
import LegalPage from './pages/LegalPage';
import UpdateChecker from './components/UpdateChecker';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import BottomNav from './components/BottomNav';
import { Toaster } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import './index.css';

const Spinner = () => (
  <div className="flex h-screen items-center justify-center bg-[var(--app-bg)]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0147a0]" />
  </div>
);

const TenantErrorScreen = ({ kind }) => {
  const { tenantSlug } = useParams();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] p-4">
      <div className="fin-card p-8 max-w-md w-full text-center" data-testid="tenant-error-screen">
        <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={28} className="text-red-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">
          {kind === 'not_found' ? 'Página não encontrada' : kind === 'suspended' ? 'Acesso suspenso' : 'Erro'}
        </h1>
        <p className="text-sm text-slate-500">
          {kind === 'not_found' && 'Este endereço não existe ou foi removido.'}
          {kind === 'suspended' && 'Seu acesso está suspenso. Entre em contato com o administrador.'}
        </p>
      </div>
    </div>
  );
};

const Layout = ({ children }) => (
  <div className="min-h-screen lg:flex bg-[#EAEFF5] dark:bg-[#000000] text-slate-800 dark:text-slate-100 antialiased font-['Plus_Jakarta_Sans',sans-serif] transition-colors duration-200 w-full max-w-full overflow-x-hidden">
    <Sidebar />
    <main className="app-main w-full flex-1 min-w-0 lg:ml-[272px] pt-3 sm:pt-4 lg:pt-4 p-3 sm:p-6 lg:p-6 lg:pl-2 min-h-screen overflow-x-hidden overflow-y-auto pb-24 lg:pb-6 max-w-full">
      {children}
    </main>
    <BottomNav />
  </div>
);

const TenantProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { loading: tLoading, error: tError } = useTenant();
  const { tenantSlug } = useParams();
  if (tLoading || loading) return <Spinner />;
  if (tError) return <TenantErrorScreen kind={tError} />;
  if (!user) return <Navigate to={`/${tenantSlug}/login`} replace />;
  return <Layout>{children}</Layout>;
};

const TenantShell = ({ children }) => (
  <TenantProvider>
    <AuthProvider>{children}</AuthProvider>
  </TenantProvider>
);

const SuperAdminProtected = ({ children }) => {
  const { admin, loading } = useSuperAdmin();
  if (loading) return <Spinner />;
  if (!admin) return <Navigate to="/superadmin/login" replace />;
  return children;
};

const HomeRedirect = () => <HomeLanding />;

function App() {
  return (
    <ThemeProvider>
      <UpdateChecker />
      <BrowserRouter>
        <Toaster
          richColors
          position="top-right"
          mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', left: 12, right: 12 }}
        />
        <PWAInstallPrompt />
        <Routes>
        {/* SuperAdmin routes */}
        <Route path="/superadmin/login" element={
          <SuperAdminProvider><SuperAdminLogin /></SuperAdminProvider>
        } />
        <Route path="/superadmin/dashboard" element={
          <SuperAdminProvider><SuperAdminProtected><SuperAdminDashboard /></SuperAdminProtected></SuperAdminProvider>
        } />
        <Route path="/superadmin" element={<Navigate to="/superadmin/login" replace />} />

        {/* Tenant routes */}
        <Route path="/:tenantSlug/login" element={
          <TenantShell><Login /></TenantShell>
        } />
        <Route path="/:tenantSlug/dashboard" element={
          <TenantShell><TenantProtectedRoute><Dashboard /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/lancamentos" element={
          <TenantShell><TenantProtectedRoute><Lancamentos /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/fluxo-caixa" element={
          <TenantShell><TenantProtectedRoute><FluxoCaixa /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/relatorios" element={
          <TenantShell><TenantProtectedRoute><Relatorios /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/tarefas" element={
          <TenantShell><TenantProtectedRoute><TarefasNotas /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/configuracoes" element={
          <TenantShell><TenantProtectedRoute><Configuracoes /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/carteira" element={
          <TenantShell><TenantProtectedRoute><Carteira /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/cartoes" element={
          <TenantShell><TenantProtectedRoute><CartaoCredito /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/desempenho" element={
          <TenantShell><TenantProtectedRoute><Desempenho /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/importar-exportar" element={
          <TenantShell><TenantProtectedRoute><ImportarExportar /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/calendario" element={
          <TenantShell><TenantProtectedRoute><Calendario /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/contas-pagar" element={
          <TenantShell><TenantProtectedRoute><ContasPagar /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/recorrencias" element={
          <TenantShell><TenantProtectedRoute><Recorrencias /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/metas" element={
          <TenantShell><TenantProtectedRoute><Metas /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/whatsapp" element={
          <TenantShell><TenantProtectedRoute><WhatsAppBot /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug/chat" element={
          <TenantShell><TenantProtectedRoute><Chat /></TenantProtectedRoute></TenantShell>
        } />
        <Route path="/:tenantSlug" element={<TenantSlugRedirect />} />

        {/* Páginas legais (públicas) */}
        <Route path="/privacidade" element={<LegalPage />} />
        <Route path="/termos" element={<LegalPage />} />
        <Route path="/cookies" element={<LegalPage />} />
        <Route path="/lgpd" element={<LegalPage />} />

        {/* Root */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </BrowserRouter>
    </ThemeProvider>
  );
}

function TenantSlugRedirect() {
  const { tenantSlug } = useParams();
  return <Navigate to={`/${tenantSlug}/dashboard`} replace />;
}

export default App;
